import * as THREE from "three";
import { G, once } from "./kit";
import { blobTex, glowTex } from "./tex";

/** Sprites, contact shadows and the special materials props share. */

const _p = new THREE.Vector3();
const _cam = new THREE.Vector3();

/**
 * A glow sprite that sits `push` metres behind its anchor as seen from the camera (set at render time): the object in
 * front hides the sprite's middle, so the glow shows as a halo around it and never slices through it.
 */
export class HaloSprite extends THREE.Sprite {
  push = 0;

  override onBeforeRender(_renderer: THREE.WebGLRenderer, _scene: THREE.Scene, camera: THREE.Camera): void {
    if (!this.push || !this.parent) return;
    _p.copy(this.position).applyMatrix4(this.parent.matrixWorld);
    _cam.setFromMatrixPosition(camera.matrixWorld);
    const away = _p.clone().sub(_cam).normalize();
    this.matrixWorld.setPosition(_p.addScaledVector(away, this.push));
  }

  override copy(source: this, recursive?: boolean): this {
    super.copy(source, recursive);
    this.push = source.push;
    return this;
  }
}

/** An additive glow sprite (named "glow"): reads from far away, pulses in `animateProp`; `push` = see HaloSprite. */
export function glowSprite(color: number | string, size: number, opacity = 0.85, push = 0): THREE.Sprite {
  const material = once(`mat:glow:${color}:${opacity}`, () => new THREE.SpriteMaterial({
    map: glowTex(), color, transparent: true, opacity, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false
  }));
  const sprite = new HaloSprite(material);
  sprite.push = push;
  sprite.scale.set(size, size, 1);
  sprite.name = "glow";
  sprite.renderOrder = 2;
  return sprite;
}

/** A soft contact shadow on the road (named "blobShadow" — hide it if the scene casts real shadows). */
export function blobShadow(width: number, depth: number, opacity = 0.9): THREE.Mesh {
  const material = once(`mat:blob:${opacity}`, () => new THREE.MeshBasicMaterial({
    map: blobTex(), transparent: true, opacity, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1
  }));
  const shadow = new THREE.Mesh(G.plane(width, depth), material);
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.y = 0.025;
  shadow.name = "blobShadow";
  shadow.renderOrder = 1;
  return shadow;
}

export interface DecalOptions {
  roughness?: number;
  metalness?: number;
  /** glows: the map is also the emissive map */
  emissive?: number;
  /** multiply with the part's vertex colour (one decal texture, many paint colours) */
  tinted?: boolean;
  /** opaque with alpha cut-out instead of blended */
  cutout?: boolean;
  normalMap?: THREE.Texture;
  side?: THREE.Side;
}

/** A material for a texture decal: lit, sits on top of its surface without z-fighting. */
export function decalMaterial(key: string, map: THREE.Texture, opts: DecalOptions = {}): THREE.MeshStandardMaterial {
  return once(`mat:decal:${key}`, () => {
    const m = new THREE.MeshStandardMaterial({
      map,
      roughness: opts.roughness ?? 0.5,
      metalness: opts.metalness ?? 0.05,
      vertexColors: opts.tinted === true,
      transparent: opts.cutout !== true,
      alphaTest: opts.cutout ? 0.5 : 0.01,
      depthWrite: opts.cutout === true,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
      normalMap: opts.normalMap ?? null,
      side: opts.side ?? THREE.FrontSide
    });
    if (opts.emissive !== undefined) {
      m.emissive = new THREE.Color(0xffffff);
      m.emissiveMap = map;
      m.emissiveIntensity = opts.emissive;
    }
    return m;
  });
}

/** An opaque textured surface material (signs printed on panels), optionally tinted by vertex colour. */
export function printMaterial(key: string, map: THREE.Texture, opts: { roughness?: number; metalness?: number; tinted?: boolean; emissive?: number; normalMap?: THREE.Texture } = {}): THREE.MeshStandardMaterial {
  return once(`mat:print:${key}`, () => {
    const m = new THREE.MeshStandardMaterial({ map, roughness: opts.roughness ?? 0.45, metalness: opts.metalness ?? 0.05, vertexColors: opts.tinted === true, normalMap: opts.normalMap ?? null });
    if (opts.emissive !== undefined) { m.emissive = new THREE.Color(0xffffff); m.emissiveMap = map; m.emissiveIntensity = opts.emissive; }
    return m;
  });
}
